import { useCallback, useEffect, useState } from 'react';
import { dealerErrorMessage, dealersApi } from '../../services/dealersApi';
import { BUYER_CREDIT_STATUSES, BuyerCredit, BuyerCreditStatus } from '../../types/dealers';
import CursorPagination from '../CursorPagination';
import DealerStatusChip from './DealerStatusChip';
import RevokeAfterPaidDialog from './RevokeAfterPaidDialog';
import { aed, formatDate, humanize } from './dealerFormat';

const PAGE_SIZE = 20;

interface CreditsTableProps {
  dealerId: string;
  token: string;
  /** Revoke-after-paid is super admin only (the server enforces it too). */
  canRevokeAfterPaid: boolean;
  notify: (message: string, type?: 'success' | 'error') => void;
  onFeatureOff: () => void;
  onChanged?: () => void;
  refreshKey?: number;
}

/**
 * Spec 085 — the buyer credits this dealer issued: totals only (BR-24), the admin sees the full name
 * and phone. Revoke after paid (exceptional) on LIVE credit with nothing held by a booking.
 */
export default function CreditsTable({ dealerId, token, canRevokeAfterPaid, notify, onFeatureOff, onChanged, refreshKey = 0 }: CreditsTableProps) {
  const [status, setStatus] = useState<BuyerCreditStatus | ''>('');
  const [rows, setRows] = useState<BuyerCredit[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<BuyerCredit | null>(null);

  const fetchPage = useCallback(async (after: string | null) => {
    if (after) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const page = await dealersApi.credits(dealerId, { status: status || undefined, cursor: after ?? undefined, limit: PAGE_SIZE }, token);
      setRows((prev) => (after ? [...prev, ...page.content] : page.content));
      setCursor(page.nextCursor ?? null);
      setHasMore(page.hasMore);
    } catch (err) {
      setError(dealerErrorMessage(err, 'Failed to load credits'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [dealerId, token, status]);

  useEffect(() => { fetchPage(null); }, [fetchPage, refreshKey]);

  return (
    <div data-testid="dealer-credits">
      <div className="admin-toolbar dealer-toolbar">
        <h3 className="admin-card-title" style={{ margin: 0 }}>Buyer credits</h3>
        <select className="admin-select" aria-label="Filter credits by status" value={status} onChange={(e) => setStatus(e.target.value as BuyerCreditStatus | '')}>
          <option value="">All statuses</option>
          {BUYER_CREDIT_STATUSES.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="admin-loading"><div className="admin-loading-spinner" /></div>
      ) : error ? (
        <div>
          <div className="admin-info-banner danger" role="alert">{error}</div>
          <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => fetchPage(null)}>Retry</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="admin-empty-state"><p>{status ? 'No credits with this status.' : 'No buyer credits yet.'}</p></div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table dealer-table">
            <thead>
              <tr>
                <th>Buyer</th><th className="num">Face value</th><th className="num">Used</th><th className="num">Held</th>
                <th className="num">Remaining</th><th>Status</th><th>Expires</th><th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const held = Number(c.reservedAed) > 0;
                return (
                  <tr key={c.id} data-testid={`credit-${c.id}`}>
                    <td>
                      <div>{c.buyerName ?? '—'}</div>
                      <div className="dealer-cell-note">{c.buyerPhone ?? ''}{c.hasAccount ? '' : ' · no app account yet'}</div>
                    </td>
                    <td className="num">{aed(c.faceValueAed)}</td>
                    <td className="num">{aed(c.usedAed)}</td>
                    <td className="num">{aed(c.reservedAed)}</td>
                    <td className="num">{aed(c.remainingAed)}</td>
                    <td><DealerStatusChip status={c.status} testId="credit-status" /></td>
                    <td>{formatDate(c.expiresAt)}</td>
                    <td className="dealer-actions">
                      {canRevokeAfterPaid && c.status === 'LIVE' && Number(c.remainingAed) > 0 && (
                        <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" disabled={held}
                          title={held ? 'Part of this credit is held by a booking. Try again later.' : undefined}
                          onClick={() => setRevoking(c)}>
                          Revoke after paid
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!loading && !error && <CursorPagination hasMore={hasMore} loading={loadingMore} onLoadMore={() => fetchPage(cursor)} />}

      {revoking && (
        <RevokeAfterPaidDialog
          dealerId={dealerId}
          target={{ kind: 'credit', id: revoking.id, label: `${revoking.buyerName ?? 'the buyer'}'s credit`, remainingAed: revoking.remainingAed }}
          token={token}
          onClose={() => setRevoking(null)}
          onRevoked={(refund) => {
            setRevoking(null);
            notify(`Revoked ${aed(refund.revokedAed)}${refund.creditNoteNumber ? ` · credit note ${refund.creditNoteNumber}` : ''}`);
            fetchPage(null);
            onChanged?.();
          }}
          onFeatureOff={onFeatureOff}
        />
      )}
    </div>
  );
}
